// The search bar's active filters as ONE ordered list: the bar chips render it and Backspace
// removes its last entry, however the filter was set (pill, typed operator, header magnifier).
import type { SearchScope, SearchSectionFilter } from "./scoped-search.svelte.js";
import { SECTION_FILTER_LABEL } from "./scoped-search.svelte.js";

export interface ActiveFilterState {
  scope: SearchScope | null;
  scopePrivate: boolean;
  sectionFilter: SearchSectionFilter | null;
  fromPeople: readonly { id: string; label: string }[];
  fromMe: boolean;
  mentionPeople: readonly { id: string; label: string }[];
  // Already gated on where Mentions me applies (not a DM scope).
  mentionsMe: boolean;
  hasLink: boolean;
  hasFile: boolean;
  // Already gated on whether every workspace is actually being searched.
  allWorkspaces: boolean;
}

export type ActiveFilterKey =
  | { kind: "in" }
  | { kind: "from"; id: string }
  | { kind: "fromMe" }
  | { kind: "mentions"; id: string }
  | { kind: "mentionsMe" }
  | { kind: "hasLink" }
  | { kind: "hasFile" }
  | { kind: "allWorkspaces" };

export interface ActiveFilter {
  id: string;
  key: ActiveFilterKey;
  label: string;
  privateChannel?: boolean;
}

export function activeFilters(s: ActiveFilterState): ActiveFilter[] {
  const out: ActiveFilter[] = [];
  if (s.scope) {
    const privateChannel = s.scope.kind === "channel" && s.scopePrivate;
    out.push({
      id: "in",
      key: { kind: "in" },
      label: `in:${s.scope.label}`,
      ...(privateChannel ? { privateChannel } : {}),
    });
  } else if (s.sectionFilter) {
    out.push({
      id: "in",
      key: { kind: "in" },
      label: `in:${SECTION_FILTER_LABEL[s.sectionFilter]}`,
    });
  }
  for (const p of s.fromPeople)
    out.push({ id: `from:${p.id}`, key: { kind: "from", id: p.id }, label: `from:${p.label}` });
  if (s.fromMe) out.push({ id: "fromMe", key: { kind: "fromMe" }, label: "from:me" });
  for (const p of s.mentionPeople)
    out.push({
      id: `mentions:${p.id}`,
      key: { kind: "mentions", id: p.id },
      label: `mentions:${p.label}`,
    });
  if (s.mentionsMe)
    out.push({ id: "mentionsMe", key: { kind: "mentionsMe" }, label: "mentions:me" });
  if (s.hasLink) out.push({ id: "hasLink", key: { kind: "hasLink" }, label: "has:link" });
  if (s.hasFile) out.push({ id: "hasFile", key: { kind: "hasFile" }, label: "has:file" });
  if (s.allWorkspaces)
    out.push({ id: "allWorkspaces", key: { kind: "allWorkspaces" }, label: "All workspaces" });
  return out;
}
